import { navigateToPage } from '../layouts/navigateTo';
import { navLabel } from '../layouts/navData';
import { useNavPalette } from './hotkeys';
import './FormLink.css';

/**
 * FormLink — render a lookup VALUE as a link to the form that owns it.
 *
 * ALPACA GoLive gets this for free: a DetailView field bound to a REFERENCE
 * property (`Requisition.Item` → `Items`, `Items.Vendor` → `Vendors`, …) is
 * drawn by XAF as a hyperlink, so a user reading "Sodium Hydroxide" on the Item
 * Requisition popup can click straight through to the Items form. AI renders
 * those same fields from flattened display names, which are plain strings — so
 * the affordance has to be asked for. This is that ask, in ONE place (standing
 * rule 8) rather than a hand-rolled anchor per form.
 *
 * WHAT IT OPENS — the RECORD's detail view when `recordId` is given, else the
 * target form's list view.
 *
 * It opened the list unconditionally until 2026-09-30, when the owner asked for
 * GoLive's behaviour: "Link these forms and those should be redirect to view the
 * detail view." XAF navigates a bound reference straight to that object's
 * DetailView (Item Initialization's Item link lands on Items_DetailView for
 * Item_00285), so stopping at the list left the user to find the row again.
 * `<ModuleListView>` now consumes the handoff and opens the record READ-ONLY —
 * the same `mode: "view"` a row click produces, matching GoLive's `&mode=View`.
 *
 * Standing rule 10 ("navigation lands on the LIST view") is about clicking a
 * SIDEBAR entry, which still lands on the list; this is a record-specific
 * hand-off, the same one Pre-Login Settings' "Sample Registration" button and
 * Client Request's Request Registration already use. Omit `recordId` and the
 * old list behaviour is unchanged.
 *
 * WHY A BUTTON, NOT AN ANCHOR: navigation is in-app. `navigateToPage` fires the
 * `ai:navigate` event that AppShell's `open()` listens for, and only `open()`
 * actually MOVES the shell — it sets the rendered form, asks "Unsaved changes
 * will be lost…" first (standing rule 15), expands the sidebar group and pushes
 * the URL. An `<a href>` would push a URL and leave the screen where it was.
 *
 * PERMISSIONS: the link is only offered for a form THIS user can open. Rather
 * than re-deriving that (AppShell's `canSee` is local to it and takes nine
 * arguments), it reads the nav-palette store AppShell publishes, which is the
 * sidebar's own gate already applied. An EMPTY store means "permissions have not
 * been published yet", not "nothing is permitted", so it fails OPEN there —
 * matching `canSeeNavItem`'s own still-booting behaviour. A form the user cannot
 * open degrades to the plain text it is today; it never becomes a dead link.
 */
export default function FormLink({
  to,
  recordId,
  value,
  fallback = 'N/A',
  className,
}: {
  /** Nav page id of the form that owns this value, e.g. `set-vendors`. */
  to: string;
  /** The record to open on that form, read-only. Omit to land on its list. */
  recordId?: string | null;
  /** The display text. Empty / null renders `fallback` as plain text. */
  value: string | null | undefined;
  /** Shown when there is no value. Never linked — there is nothing to open. */
  fallback?: string;
  /** Extra class on the rendered element, so a form keeps its own text style. */
  className?: string;
}) {
  const canOpen = useNavPalette(
    (s) => s.items.length === 0 || s.items.some((i) => i.id === to),
  );

  const text = value == null || value === '' ? '' : value;
  if (text === '' || !canOpen) {
    return <span className={className}>{text === '' ? fallback : text}</span>;
  }

  const target = navLabel(to);
  return (
    <button
      type="button"
      className={className ? `${className} form-link` : 'form-link'}
      title={`Open ${target}`}
      onClick={() => navigateToPage(to, recordId ?? undefined)}
    >
      {text}
    </button>
  );
}
